import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/auth';
import { useConfig } from '@/contexts/ConfigContext';
import { useTheme } from '@/contexts/ThemeContext';
import { LogIn, CheckCircle } from 'lucide-react';

export function Logout() {
  const navigate = useNavigate();
  const { config } = useConfig();
  const { resolvedTheme } = useTheme();
  const [countdown, setCountdown] = useState(5);
  const [autoRedirect, setAutoRedirect] = useState(true);

  const logoUrl = resolvedTheme === 'dark' ? config.logo_dark_url : config.logo_light_url;

  useEffect(() => {
    // Check if this is an intentional logout (vs. session expiry)
    const intentionalLogout = authService.hasIntentionallyLoggedOut();

    if (!intentionalLogout) {
      // If not intentional logout, redirect to login immediately
      navigate('/login', { replace: true });
      return;
    }

    // Only show countdown for intentional logout
    if (autoRedirect) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Clear the flag and directly initiate SSO login
            // This avoids requiring the user to click "Sign in with SSO" again
            authService.clearLogoutFlag();
            authService.login();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [navigate, autoRedirect]);

  const handleSignInAgain = () => {
    authService.clearLogoutFlag();
    // Directly initiate SSO login instead of navigating to login page
    // This avoids requiring the user to click "Sign in with SSO" again
    authService.login();
  };

  const handleStayLoggedOut = () => {
    setAutoRedirect(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={config.portal_name || 'Product Portal'}
              className="mx-auto h-16 w-auto"
            />
          ) : (
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {config.portal_name || 'Product Portal'}
            </h1>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 py-8 px-6 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 text-center">
          <div className="mb-4">
            <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            You've been signed out
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Your session has ended successfully.
          </p>

          {autoRedirect && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Redirecting to login in {countdown} seconds...
            </p>
          )}

          <div className="space-y-3">
            <button
              onClick={handleSignInAgain}
              className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              <LogIn className="mr-2 h-4 w-4" />
              Sign In Again
            </button>

            {autoRedirect && (
              <button
                onClick={handleStayLoggedOut}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                Stay on this page
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
